import { Injectable } from '@angular/core';
import { B2BCompany, FHService, Price, Product } from 'npx-family-happy-common';
import { Observable, forkJoin, map, of, switchMap } from 'rxjs';
import { DEFAULT_PRICE_FILTERS, PriceCreationBody, PriceService } from '../../prices/prices-service/price.service';
import { ProductServiceDashboard } from '../../products/product-services/product-services';
import { DEFAULT_SERVICES_FILTERS, ServiceServices } from '../../services/service-services/service.services';
import { CatalogFHService, CatalogProduct, CatalogService } from './catalog.model';
import { isB2BService } from './convention.model';

/** Più che sufficiente per i servizi/prodotti/prezzi di una singola azienda. */
const PAGE_SIZE = 200;

/**
 * Servizi, prodotti e prezzi di un'azienda B2B con gli endpoint esistenti (una chiamata per servizio e per prodotto).
 *
 * Le liste del backend restituiscono il servizio senza `b2bCompany`: prima di ogni salvataggio va rimessa,
 * anche dentro prodotto e prezzo, altrimenti il servizio rischia di essere scollegato dall'azienda.
 */
@Injectable({
  providedIn: 'root',
})
export class B2BCompanyCatalogService {
  constructor(
    private serviceServices: ServiceServices,
    private productService: ProductServiceDashboard,
    private priceService: PriceService
  ) {}

  load(companyId: string): Observable<CatalogService[]> {
    return this.serviceServices
      .getAllServices(0, PAGE_SIZE, { ...DEFAULT_SERVICES_FILTERS, b2bCompanyId: companyId })
      .pipe(
        map((page) => page.content as CatalogFHService[]),
        switchMap((services) =>
          services.length ? forkJoin(services.map((service) => this.loadService(service))) : of([])
        ),
        map((items) => items.sort((a, b) => a.service.name.localeCompare(b.service.name)))
      );
  }

  /** I prodotti dei servizi B2B non hanno prezzi di listino (il prezzo sta nella convenzione): niente chiamate ai prezzi. */
  private loadService(service: CatalogFHService): Observable<CatalogService> {
    const b2b = isB2BService(service);
    return this.productService.getAllProducts(0, PAGE_SIZE, { serviceID: service.id, searchKey: null }).pipe(
      switchMap((page) => {
        if (b2b) return of(page.content.map((product) => ({ product, prices: [] })));
        return page.content.length ? forkJoin(page.content.map((product) => this.loadProduct(product))) : of([]);
      }),
      map((products: CatalogProduct[]) => ({ service, products }))
    );
  }

  private loadProduct(product: Product): Observable<CatalogProduct> {
    return this.priceService
      .getAllPrices(0, PAGE_SIZE, { ...DEFAULT_PRICE_FILTERS, productID: product.id })
      .pipe(map((page) => ({ product, prices: page.content })));
  }

  /* --- Servizi ----------------------------------------------------------------------------------- */

  searchServices(searchKey: string) {
    return this.serviceServices
      .getAllServices(0, 20, { ...DEFAULT_SERVICES_FILTERS, searchKey: searchKey || null })
      .pipe(map((page) => page.content as CatalogFHService[]));
  }

  /** Dettaglio con `b2bCompany`: serve per sapere se un servizio è già collegato a un'altra azienda. */
  getService(id: number) {
    return this.serviceServices.getServiceDetailsByID(id) as Observable<CatalogFHService>;
  }

  /** Crea (senza id) o aggiorna il servizio, sempre collegato a `company`. */
  saveService(service: Partial<CatalogFHService>, company: B2BCompany) {
    return this.serviceServices.saveService({ ...service, b2bCompany: company } as FHService);
  }

  /* --- Prodotti ---------------------------------------------------------------------------------- */

  searchProducts(searchKey: string) {
    return this.productService
      .getAllProducts(0, 20, { serviceID: null, searchKey: searchKey || null })
      .pipe(map((page) => page.content));
  }

  /** Crea (senza id) o aggiorna il prodotto dentro `service`. Collegare un prodotto esistente = cambiargli servizio. */
  saveProduct(product: Partial<Product>, service: CatalogFHService, company: B2BCompany) {
    return this.productService.saveProduct({ ...product, service: { ...service, b2bCompany: company } } as Product);
  }

  deleteProduct(id: number) {
    return this.productService.deleteProductById(id);
  }

  /* --- Prezzi ------------------------------------------------------------------------------------ */

  /** Stessi filtri della pagina Prezzi: il prodotto si sceglie con app-product-select e si filtra per `productID`. */
  searchPrices(filters: { productID: number | null; stripeID: string; province: string | null }) {
    return this.priceService
      .getAllPrices(0, 50, { ...DEFAULT_PRICE_FILTERS, ...filters })
      .pipe(map((page) => page.content));
  }

  /** Un prezzo per ogni provincia, come la pagina Prezzi. `amount` in centesimi. */
  createPrices(
    body: { amount: number; stripeID: string; provinces: string[] },
    product: Product,
    service: CatalogFHService,
    company: B2BCompany
  ) {
    const creation: PriceCreationBody = { ...body, product: this.productWithCompany(product, service, company) };
    return this.priceService.createPrice(creation);
  }

  /** Aggiorna il prezzo, o lo collega a `product` se arriva da un altro prodotto. */
  updatePrice(price: Price, product: Product, service: CatalogFHService, company: B2BCompany) {
    return this.priceService.updatePrice({ ...price, product: this.productWithCompany(product, service, company) });
  }

  deletePrice(id: number) {
    return this.priceService.deletePriceById(id);
  }

  private productWithCompany(product: Product, service: CatalogFHService, company: B2BCompany): Product {
    return { ...product, service: { ...service, b2bCompany: company } };
  }
}
